import { useState } from 'react';
import {
  AdminEnterprisesScreen_EnterpriseFragment,
  AdminEnterprisesWithLicenseScreenDocument,
  AdminEnterprisesWithoutLicenseScreenDocument,
  StandardItemType,
  useEnterpriseStandardsEditor_UpdateStandardsMutation,
} from '@/graphql/generated';
import {
  EnterpriseStandardsEditor_StandardFragment,
  useEnterpriseStandardsEditor_StandardsQuery,
} from '@/graphql/cms/generated';
import { cmsClient } from '@/graphql/clients/cmsClient';
import { MessageBox, MessageBoxType } from '@/components/layout/MessageBox';
import { Loader } from '@/components/generic/Loader';
import { Toggle } from '@/components/generic/form/Toggle';
import { useTranslation } from '@/hooks/useTranslation';
import { useToast } from '@/components/layout/Toast';
import { LoaderFullscreen } from '@/components/layout/Loader';
import { HelpTooltip } from '@/components/generic/HelpTooltip';

export function EnterpriseStandardsEditor({
  company,
}: {
  company: AdminEnterprisesScreen_EnterpriseFragment;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [updatingSlug, setUpdatingSlug] = useState<string | null>(null);

  const standardsQuery = useEnterpriseStandardsEditor_StandardsQuery({
    client: cmsClient,
    fetchPolicy: 'cache-and-network',
  });

  const [updateStandardsMutation] =
    useEnterpriseStandardsEditor_UpdateStandardsMutation();

  // Get current customStandards slugs
  const customStandardsSlugs = new Set(
    company.customStandards?.map((link) => link.slug).filter(Boolean) || [],
  );

  const handleToggle = async (
    standard: EnterpriseStandardsEditor_StandardFragment,
    isEnabled: boolean,
  ) => {
    if (!standard?.slug) return;

    setUpdatingSlug(standard.slug);

    // Get current customStandards
    const currentStandards = company.customStandards || [];

    let newStandards: Array<{ slug: string; type: StandardItemType }>;

    if (isEnabled) {
      // Add the standard if it's not already present
      if (!customStandardsSlugs.has(standard.slug)) {
        newStandards = [
          ...currentStandards,
          {
            slug: standard.slug,
            type: StandardItemType.Standard,
          },
        ];
      } else {
        setUpdatingSlug(null);
        return;
      }
    } else {
      // Remove the standard
      newStandards = currentStandards.filter(
        (link) => link.slug !== standard.slug,
      );
    }

    try {
      await updateStandardsMutation({
        variables: {
          input: {
            id: company.id,
            customStandards: newStandards,
          },
        },
        refetchQueries: [
          AdminEnterprisesWithLicenseScreenDocument,
          AdminEnterprisesWithoutLicenseScreenDocument,
        ],
      });

      toast.openToastWithMessage(t('toast:company.settings.updated.success'));
    } catch (error) {
      toast.openToastWithError(
        error instanceof Error ? error.message : t('global:error'),
      );
    } finally {
      setUpdatingSlug(null);
    }
  };

  if (standardsQuery.loading) {
    return <LoaderFullscreen />;
  }

  if (standardsQuery.error) {
    return (
      <MessageBox type={MessageBoxType.Error}>
        {standardsQuery.error.message}
      </MessageBox>
    );
  }

  const standards = standardsQuery.data?.standards || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <h4>{t('company.settings.standards.title')}</h4>
        <HelpTooltip>{t('company.settings.standards.info')}</HelpTooltip>
      </div>
      {standards.length === 0 ? (
        <p className="text-gray-600">
          {t('company.settings.standards.noStandards')}
        </p>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {standards.map((standard) => {
            if (!standard) return null;

            const isEnabled = customStandardsSlugs.has(standard.slug);
            const isUpdating = updatingSlug === standard.slug;

            return (
              <div
                key={standard.slug}
                className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
              >
                <div className="flex-1">
                  <div className="text-sm">{standard.name}</div>
                </div>
                <div className="ml-4 flex items-center">
                  {isUpdating ? (
                    <Loader />
                  ) : (
                    <Toggle
                      state={isEnabled}
                      setState={(newState) => handleToggle(standard, newState)}
                      disabled={updatingSlug !== null}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
